import { NUMEROLOGY_CHAPTERS } from 'schools/numerology/data/numerologySyllabus';
import { buildSchoolTopicKey } from 'shared/schoolLearn/schoolTopicEngagement';
import { readStudentProfile } from './studentProfileStorage';
import { readLastNumerologyTopic } from './numerologyHomeHub';
import { isNumerologyTopicId, numerologyTopicStorageKey } from './topicTrack';
import {
  isSchoolLearnTopicKey,
  parseSchoolTopicKey,
} from 'shared/schoolLearn/schoolTopicEngagement';

const NUMEROLOGY_TOPIC_KEYS = NUMEROLOGY_CHAPTERS.flatMap((chapter) =>
  chapter.topics.map((topic) => buildSchoolTopicKey('numerology', topic.id)),
);

/** @param {string} topicId */
function isJyotishaOnlyTopicId(topicId) {
  return !isNumerologyTopicId(topicId) && !isSchoolLearnTopicKey(topicId);
}

/** @param {string} topicId @param {'jaimini' | 'bnn' | 'kp' | 'lal-kitab' | 'numerology'} school */
function isSchoolTopicId(topicId, school) {
  const parsed = parseSchoolTopicKey(topicId);
  return parsed?.school === school;
}

function resolveNumerologyProgressKey(topicId) {
  return numerologyTopicStorageKey(topicId);
}

export function getNumerologyProgressSummary() {
  const state = readStudentProfile();
  const completed = NUMEROLOGY_TOPIC_KEYS.filter(
    (id) => state.topicProgress[id]?.status === 'completed',
  ).length;
  const inProgress = NUMEROLOGY_TOPIC_KEYS.filter(
    (id) => state.topicProgress[id]?.status === 'in_progress',
  ).length;
  const total = NUMEROLOGY_TOPIC_KEYS.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, inProgress, total, pct };
}

export function getNumerologyContinueLearning() {
  const state = readStudentProfile();
  let lastId = readLastNumerologyTopic();

  if (!lastId || !isNumerologyTopicId(lastId)) {
    const fromRecent = state.recentTopics.find((r) => isNumerologyTopicId(r.topicId))?.topicId;
    if (fromRecent) {
      lastId = fromRecent;
    } else {
      const [inProgress] = getNumerologyInProgressTopics(1);
      lastId = inProgress?.topicId ?? null;
    }
  }

  if (!lastId || !isNumerologyTopicId(lastId)) return null;
  const storageKey = resolveNumerologyProgressKey(lastId);
  const progress = state.topicProgress[storageKey];
  return {
    topicId: storageKey,
    scrollY: progress?.scrollY ?? 0,
    lastVisitedAt: progress?.lastVisitedAt ?? null,
    status: progress?.status ?? 'in_progress',
  };
}

export function getNumerologyGroupProgress() {
  const state = readStudentProfile();
  return NUMEROLOGY_CHAPTERS.map((chapter) => {
    const ids = chapter.topics.map((t) => buildSchoolTopicKey('numerology', t.id));
    const completed = ids.filter((id) => state.topicProgress[id]?.status === 'completed').length;
    return {
      id: chapter.id,
      label: chapter.title.hi,
      labelEn: chapter.title.en,
      completed,
      total: ids.length,
      pct: ids.length > 0 ? Math.round((completed / ids.length) * 100) : 0,
    };
  });
}

/** @param {number} [limit] */
export function getNumerologyRecommendedTopics(limit = 3) {
  const state = readStudentProfile();
  const lastRaw = readLastNumerologyTopic();
  const lastSlug = lastRaw
    ? parseSchoolTopicKey(lastRaw)?.topicId ?? lastRaw
    : null;
  let pool = NUMEROLOGY_TOPIC_KEYS;

  if (lastSlug) {
    const chapter = NUMEROLOGY_CHAPTERS.find((c) => c.topics.some((t) => t.id === lastSlug));
    if (chapter) {
      const groupIds = chapter.topics.map((t) => buildSchoolTopicKey('numerology', t.id));
      const incompleteInGroup = groupIds.filter(
        (id) => state.topicProgress[id]?.status !== 'completed',
      );
      if (incompleteInGroup.length > 0) pool = incompleteInGroup;
    }
  }

  const bookmarked = state.bookmarks
    .map((b) => b.topicId)
    .filter(isNumerologyTopicId)
    .map(resolveNumerologyProgressKey);
  const candidates = [...new Set([...bookmarked, ...pool])].filter(
    (id) => state.topicProgress[id]?.status !== 'completed',
  );
  return candidates.slice(0, limit);
}

/** @param {import('./studentProfileStorage').StudentProfileState['bookmarks']} bookmarks */
export function filterNumerologyBookmarks(bookmarks) {
  return bookmarks.filter((b) => isNumerologyTopicId(b.topicId));
}

/** @param {import('./studentProfileStorage').StudentProfileState['highlights']} highlights */
export function filterNumerologyHighlights(highlights) {
  return highlights.filter((h) => isNumerologyTopicId(h.topicId));
}

/** @param {import('./studentProfileStorage').StudentProfileState['bookmarks']} bookmarks */
export function filterJyotishaBookmarks(bookmarks) {
  return bookmarks.filter((b) => isJyotishaOnlyTopicId(b.topicId));
}

/** @param {import('./studentProfileStorage').StudentProfileState['highlights']} highlights */
export function filterJyotishaHighlights(highlights) {
  return highlights.filter((h) => isJyotishaOnlyTopicId(h.topicId));
}

/** @param {Array<{ topicId: string }>} queue */
export function filterJyotishaRevisionQueue(queue) {
  return queue.filter((h) => isJyotishaOnlyTopicId(h.topicId));
}

/** @param {import('./studentProfileStorage').StudentProfileState['bookmarks']} bookmarks @param {'jaimini' | 'bnn' | 'kp' | 'lal-kitab' | 'numerology'} school */
export function filterSchoolBookmarks(bookmarks, school) {
  return bookmarks.filter((b) => isSchoolTopicId(b.topicId, school));
}

/** @param {import('./studentProfileStorage').StudentProfileState['highlights']} highlights @param {'jaimini' | 'bnn' | 'kp' | 'lal-kitab' | 'numerology'} school */
export function filterSchoolHighlights(highlights, school) {
  return highlights.filter((h) => isSchoolTopicId(h.topicId, school));
}

/** @param {Array<{ topicId: string }>} queue @param {'jaimini' | 'bnn' | 'kp' | 'lal-kitab' | 'numerology'} school */
export function filterSchoolRevisionQueue(queue, school) {
  return queue.filter((h) => isSchoolTopicId(h.topicId, school));
}

/** @param {Array<{ topicId: string }>} queue */
export function filterNumerologyRevisionQueue(queue) {
  return queue.filter((h) => isNumerologyTopicId(h.topicId));
}

export function getNumerologyInProgressTopics(limit = 12) {
  const state = readStudentProfile();
  const items = NUMEROLOGY_TOPIC_KEYS.filter(
    (id) => state.topicProgress[id]?.status === 'in_progress',
  ).map((id) => ({
    topicId: id,
    lastVisitedAt: state.topicProgress[id]?.lastVisitedAt ?? null,
    scrollY: state.topicProgress[id]?.scrollY ?? 0,
  }));
  items.sort((a, b) => (b.lastVisitedAt ?? '').localeCompare(a.lastVisitedAt ?? ''));
  return items.slice(0, limit);
}
